import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { SkeletonRows } from 'components/Skeleton'
import {
  getLegislations, getLegislationProgramme,
  exportLegislationArchive, exportLegislationPack,
} from 'services/legislationService'
import { scoreColour, formatPercent } from 'features/dashboard/charts/scoreColour'
import { cadenceLabel, PENALTY_SEVERITIES, LEGISLATION_TYPE_META, legislationTypeMeta } from 'features/compliance/obligationConstants'
import { downloadBlob } from 'utils/downloadBlob'

const TYPE_META = LEGISLATION_TYPE_META

const CRIT = {
  critical: { fg: '#b00020', bg: '#fdecea', label: 'Critical', help: 'Critical: a serious safety or legal risk if this requirement is not met.' },
  high:     { fg: '#c2410c', bg: '#ffedd5', label: 'High', help: 'High: an important requirement with significant risk if not met.' },
  medium:   { fg: '#d97706', bg: '#fef3c7', label: 'Medium', help: 'Medium: a moderate requirement to stay on top of.' },
  low:      { fg: '#15803d', bg: '#dcfce7', label: 'Low', help: 'Low: a lower-risk requirement.' },
}

const STATUS = {
  satisfied: { fg: '#15803d', label: 'Satisfied', help: 'Satisfied: current evidence or completed work is on file.' },
  pending:   { fg: '#d97706', label: 'Pending', help: 'Pending: not yet evidenced, or due soon.' },
  expired:   { fg: '#b00020', label: 'Expired', help: 'Expired: the evidence or work is out of date and needs renewing.' },
  in_review: { fg: '#d97706', label: 'In review', help: 'In review: evidence has been uploaded and is awaiting a decision.' },
  skipped:   { fg: '#64748b', label: 'Not applicable', help: 'Not applicable: marked as not relevant to this building.' },
}

const typeMeta = legislationTypeMeta
const basisLabel = b => b === 'statutory' ? 'Set by law' : b === 'risk_assessment' ? 'Risk-assessment based' : b === 'guidance' ? 'Guidance-based' : null
const fmtDate = d => {
  if (!d) return ''
  const t = new Date(d)
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const ByLegislation = ({ bpId }) => {
  const [legislations, setLegislations] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [expanded, setExpanded] = useState(() => new Set())
  const [oblsByFw, setOblsByFw] = useState({})
  const [oblLoading, setOblLoading] = useState({})
  const [downloading, setDownloading] = useState(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    getLegislations(bpId)
      .then(res => { if (active) setLegislations(res.data?.data || []) })
      .catch(() => { if (active) setLegislations([]) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [bpId])

  const loadObls = useCallback(fw => {
    setOblLoading(m => ({ ...m, [fw]: true }))
    getLegislationProgramme(bpId, fw)
      .then(res => {
        const obls = (res.data?.data?.domains || []).flatMap(d => d.obligations || [])
        setOblsByFw(m => ({ ...m, [fw]: obls }))
      })
      .catch(() => setOblsByFw(m => ({ ...m, [fw]: [] })))
      .finally(() => setOblLoading(m => ({ ...m, [fw]: false })))
  }, [bpId])

  const toggle = fw => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(fw)) {
      next.delete(fw)
    } else {
      next.add(fw)
      if (oblsByFw[fw] === undefined && !oblLoading[fw]) loadObls(fw)
    }
    return next
  })

  const download = async (fw, kind) => {
    setDownloading(`${fw}:${kind}`)
    try {
      const call = kind === 'pack' ? exportLegislationPack : exportLegislationArchive
      const res = await call(bpId, fw)
      downloadBlob(res.data, `${fw}-${kind === 'pack' ? 'submission-pack' : 'evidence'}.zip`)
    } catch (e) {
      toast.error('Could not generate the download.')
    } finally {
      setDownloading(null)
    }
  }

  const typeCounts = useMemo(() => {
    const c = {}
    legislations.forEach(l => { const t = l.type || 'other'; c[t] = (c[t] || 0) + 1 })
    return c
  }, [legislations])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return legislations.filter(l => {
      if (typeFilter !== 'all' && (l.type || 'other') !== typeFilter) return false
      if (q && !`${l.framework} ${(l.sections || []).join(' ')}`.toLowerCase().includes(q)) return false
      return true
    })
  }, [legislations, search, typeFilter])

  if (loading) return <SkeletonRows rows={6} />
  if (legislations.length === 0) {
    return <Note>No tracked obligations yet. Once this building has a compliance programme, its legislation will appear here.</Note>
  }

  return (
    <Wrap>
      <Toolbar>
        <SearchInput placeholder='Search legislation…' value={search} onChange={e => setSearch(e.target.value)} />
        <Chips>
          <Chip $active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>All <b>{legislations.length}</b></Chip>
          {Object.keys(TYPE_META).filter(t => typeCounts[t]).map(t => (
            <Chip key={t} $active={typeFilter === t} $c={typeMeta(t).fg}
              onClick={() => setTypeFilter(typeFilter === t ? 'all' : t)}>
              {typeMeta(t).label} <b>{typeCounts[t]}</b>
            </Chip>
          ))}
        </Chips>
        <Count>Showing {filtered.length} of {legislations.length}</Count>
      </Toolbar>

      <List>
        {filtered.map(l => {
          const tm = typeMeta(l.type)
          const isOpen = expanded.has(l.framework)
          const obls = oblsByFw[l.framework]
          return (
            <Item key={l.framework} $open={isOpen}>
              <Row onClick={() => toggle(l.framework)}>
                <Chevron $open={isOpen}><ChevronRightRoundedIcon style={{ fontSize: 20 }} /></Chevron>
                <TypeBadge $fg={tm.fg} $bg={tm.bg} title={tm.desc}>{tm.label}</TypeBadge>
                <RowName>{l.framework}</RowName>
                <StatTag $statutory={l.isStatutory}>{l.isStatutory ? 'Statutory' : 'Non-statutory'}</StatTag>
                <RowCounts>
                  {l.satisfied}/{l.total} satisfied
                  {l.overdue > 0 ? ` · ${l.overdue} overdue` : ''}
                  {(l.sections?.length ?? 0) > 1 ? ` · ${l.sections.length} refs` : ''}
                </RowCounts>
                <Score $c={scoreColour(l.score)}>{formatPercent(l.score)}</Score>
              </Row>

              {isOpen && (
                <Detail>
                  <TypeExplain $fg={tm.fg} $bg={tm.bg}>
                    <GavelRoundedIcon style={{ fontSize: 15 }} />
                    <span>
                      <strong>{tm.label}.</strong> {tm.desc}{' '}
                      {l.isStatutory
                        ? 'This is a legal requirement for this building.'
                        : 'Strongly advised as good practice, but not a direct legal requirement.'}
                    </span>
                  </TypeExplain>

                  <DetailMeta>
                    {(l.sections?.length ?? 0) > 0 && (
                      <MetaBlock><MetaLabel>References</MetaLabel><MetaVal>{l.sections.join(' · ')}</MetaVal></MetaBlock>
                    )}
                    <MetaBlock>
                      <MetaLabel>Status</MetaLabel>
                      <MetaVal>
                        {l.satisfied} satisfied · {l.pending} pending
                        {l.in_review ? ` · ${l.in_review} in review` : ''}
                        {l.overdue ? ` · ${l.overdue} overdue` : ''}
                        {l.skipped ? ` · ${l.skipped} not applicable` : ''}
                      </MetaVal>
                    </MetaBlock>
                    {l.citationUrl && (
                      <MetaBlock>
                        <MetaLabel>Source</MetaLabel>
                        <CiteLink href={l.citationUrl} target='_blank' rel='noopener noreferrer'>
                          Read legislation <OpenInNewRoundedIcon style={{ fontSize: 12 }} />
                        </CiteLink>
                      </MetaBlock>
                    )}
                  </DetailMeta>

                  <ExportRow>
                    <ExportBtn disabled={!!downloading} onClick={() => download(l.framework, 'archive')}>
                      <DownloadRoundedIcon style={{ fontSize: 15 }} />
                      {downloading === `${l.framework}:archive` ? 'Preparing…' : 'Evidence archive'}
                    </ExportBtn>
                    <ExportBtn $primary disabled={!!downloading} onClick={() => download(l.framework, 'pack')}>
                      <DownloadRoundedIcon style={{ fontSize: 15 }} />
                      {downloading === `${l.framework}:pack` ? 'Preparing…' : 'Submission pack'}
                    </ExportBtn>
                  </ExportRow>

                  <OblHead>Requirements under this {tm.label.toLowerCase()}</OblHead>
                  {oblLoading[l.framework] || obls === undefined ? (
                    <SkeletonRows rows={3} />
                  ) : obls.length === 0 ? (
                    <Muted>No obligations found.</Muted>
                  ) : (
                    <Obls>{obls.map(o => <ObligationDetail key={o.id} o={o} framework={l.framework} />)}</Obls>
                  )}
                </Detail>
              )}
            </Item>
          )
        })}
        {filtered.length === 0 && <Muted>No legislation matches your filters.</Muted>}
      </List>
    </Wrap>
  )
}

const ObligationDetail = ({ o, framework }) => {
  const crit = CRIT[o.criticality] || CRIT.medium
  const st = STATUS[o.status] || { fg: '#64748b', label: o.status, help: 'Current status of this requirement.' }
  const penalty = o.penaltySeverity && o.penaltySeverity !== 'none'
    ? (PENALTY_SEVERITIES[o.penaltySeverity]?.label || o.penaltySeverity)
    : null
  const isFile = o.obligationKind === 'file_type'
  const isUnit = o.scope === 'unit'
  const cov = o.unitCoverage
  const basis = basisLabel(o.cadenceBasis)
  const specificRef = o.regulatoryFramework && o.regulatoryFramework !== framework ? o.regulatoryFramework : null

  return (
    <OblCard>
      <OblTop>
        <StatusDot $c={st.fg} />
        <OblName>
          {o.citationUrl
            ? <CiteLink href={o.citationUrl} target='_blank' rel='noopener noreferrer'>{o.name} <OpenInNewRoundedIcon style={{ fontSize: 12 }} /></CiteLink>
            : o.name}
        </OblName>
        <CritChip $fg={crit.fg} $bg={crit.bg} title={crit.help}>{crit.label}</CritChip>
      </OblTop>
      {o.description && <OblDesc>{o.description}</OblDesc>}
      <OblChips>
        <OChip>{isFile ? (isUnit ? 'Per-unit document' : 'Document') : 'Task'}</OChip>
        <OChip>{o.cadenceDays ? cadenceLabel(o.cadenceDays) : 'One-off'}{basis ? ` · ${basis}` : ''}</OChip>
        {penalty && <OChip $warn>Penalty: {penalty}</OChip>}
        {isUnit && cov && (cov.applicable ?? 0) > 0 && <OChip>{cov.covered}/{cov.applicable} units covered</OChip>}
        {specificRef && <OChip>{specificRef}</OChip>}
      </OblChips>
      <OblFoot>
        <FootStatus $c={st.fg} title={st.help}>{st.label}</FootStatus>
        {o.nextDueAt && <FootMeta>Next due {fmtDate(o.nextDueAt)}</FootMeta>}
        {o.lastSatisfiedAt && <FootMeta>Last satisfied {fmtDate(o.lastSatisfiedAt)}</FootMeta>}
        {(o.evidence?.name || o.evidence?.title) && <FootMeta>Evidence: {o.evidence.name || o.evidence.title}</FootMeta>}
        {o.lastVerifiedAt && (
          <FootMeta>Curated {fmtDate(o.lastVerifiedAt)}{o.reviewDueAt ? ` · review due ${fmtDate(o.reviewDueAt)}` : ''}</FootMeta>
        )}
      </OblFoot>
    </OblCard>
  )
}

export default ByLegislation

const Wrap = styled.div``
const Toolbar = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 14px;
`
const SearchInput = styled.input`
  font: inherit; font-size: 13px; padding: 8px 12px;
  border: 1px solid #ececec; border-radius: 10px; outline: none; min-width: 220px;
  &:focus { border-color: #2aa6b3; }
`
const Chips = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `
const Chip = styled.button`
  font: inherit; cursor: pointer;
  font-size: 12px; font-weight: 700; padding: 5px 11px; border-radius: 999px;
  color: ${({ $active, $c }) => ($active ? '#fff' : ($c || '#475467'))};
  background: ${({ $active, $c }) => ($active ? ($c || '#0f172a') : '#fff')};
  border: 1px solid ${({ $active, $c }) => ($active ? ($c || '#0f172a') : '#e6e8ec')};
  b { font-weight: 800; opacity: 0.85; margin-left: 2px; }
`
const Count = styled.span` margin-left: auto; font-size: 11.5px; font-weight: 700; color: #8a909b; `

const List = styled.div` display: flex; flex-direction: column; gap: 10px; `
const Item = styled.div`
  border: 1px solid ${({ $open }) => ($open ? '#d8dce2' : '#e9ebef')};
  border-radius: 12px; background: #fff; overflow: hidden;
`
const Row = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; cursor: pointer;
  &:hover { background: #fafbfc; }
`
const Chevron = styled.span`
  display: inline-flex; color: #98a2b3; flex-shrink: 0;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')}); transition: transform 0.15s ease;
`
const TypeBadge = styled.span`
  flex-shrink: 0; cursor: help;
  font-size: 10.5px; font-weight: 800; letter-spacing: 0.02em;
  padding: 3px 10px; border-radius: 999px;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg}; border: 1px solid ${({ $fg }) => `${$fg}33`};
`
const RowName = styled.span` flex: 1; min-width: 0; font-size: 14px; font-weight: 700; color: #1d2433; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; `
const StatTag = styled.span`
  flex-shrink: 0; font-size: 10px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase;
  padding: 2px 8px; border-radius: 999px;
  color: ${({ $statutory }) => ($statutory ? '#1f5fbf' : '#64748b')};
  background: ${({ $statutory }) => ($statutory ? '#e8f1fd' : '#f1f3f7')};
  border: 1px solid ${({ $statutory }) => ($statutory ? '#1f5fbf33' : '#64748b33')};
`
const RowCounts = styled.span` flex-shrink: 0; font-size: 12px; color: #667085; @media (max-width: 720px) { display: none; } `
const Score = styled.span`
  flex-shrink: 0; font-weight: 800; font-size: 12px; padding: 2px 10px; border-radius: 999px;
  color: ${({ $c }) => $c}; background: ${({ $c }) => `${$c}14`}; border: 1px solid ${({ $c }) => `${$c}40`};
`

const Detail = styled.div` padding: 4px 14px 16px; display: flex; flex-direction: column; gap: 12px; border-top: 1px solid #f0f0f2; `
const TypeExplain = styled.div`
  display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; border-radius: 10px;
  font-size: 12.5px; line-height: 1.5;
  color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg}; border: 1px solid ${({ $fg }) => `${$fg}26`};
  svg { flex-shrink: 0; margin-top: 1px; }
  strong { font-weight: 800; }
`
const DetailMeta = styled.div` display: flex; flex-wrap: wrap; gap: 20px; padding: 0 2px; `
const MetaBlock = styled.div` display: flex; flex-direction: column; gap: 3px; min-width: 0; `
const MetaLabel = styled.span` font-size: 10px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #98a2b3; `
const MetaVal = styled.span` font-size: 12.5px; color: #475467; `
const CiteLink = styled.a` display: inline-flex; align-items: center; gap: 3px; color: #2aa6b3; font-weight: 600; text-decoration: none; &:hover { text-decoration: underline; } `

const ExportRow = styled.div` display: flex; gap: 8px; flex-wrap: wrap; `
const ExportBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
  font-size: 12px; font-weight: 700; padding: 7px 12px; border-radius: 8px;
  border: 1px solid ${({ $primary }) => ($primary ? '#2aa6b3' : '#dfe3e8')};
  background: ${({ $primary }) => ($primary ? '#2aa6b3' : '#fff')};
  color: ${({ $primary }) => ($primary ? '#fff' : '#475467')};
  &:disabled { opacity: 0.55; cursor: default; }
  &:hover:not(:disabled) { filter: brightness(0.97); }
`

const OblHead = styled.div` font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #8a909b; margin-top: 2px; `
const Obls = styled.div` display: flex; flex-direction: column; gap: 8px; `
const OblCard = styled.div` border: 1px solid #eef0f3; border-radius: 10px; padding: 11px 13px; display: flex; flex-direction: column; gap: 7px; background: #fcfcfd; `
const OblTop = styled.div` display: flex; align-items: center; gap: 8px; `
const StatusDot = styled.span` width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: ${({ $c }) => $c}; `
const OblName = styled.span` flex: 1; min-width: 0; font-size: 13.5px; font-weight: 700; color: #1d2433; `
const CritChip = styled.span` flex-shrink: 0; font-size: 10px; font-weight: 800; padding: 2px 8px; border-radius: 999px; color: ${({ $fg }) => $fg}; background: ${({ $bg }) => $bg}; `
const OblDesc = styled.p` margin: 0; font-size: 12.5px; line-height: 1.5; color: #475467; `
const OblChips = styled.div` display: flex; flex-wrap: wrap; gap: 6px; `
const OChip = styled.span`
  font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px;
  color: ${({ $warn }) => ($warn ? '#b54708' : '#475467')};
  background: ${({ $warn }) => ($warn ? '#fef3c7' : '#eef0f3')};
`
const OblFoot = styled.div` display: flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; `
const FootStatus = styled.span` font-size: 11.5px; font-weight: 800; color: ${({ $c }) => $c}; text-transform: capitalize; `
const FootMeta = styled.span` font-size: 11px; color: #8a909b; `
const Muted = styled.div` padding: 16px; text-align: center; font-size: 13px; color: #94a3b8; `
const Note = styled.div` padding: 24px; text-align: center; color: #4A4F5E; `
